import { Select, Table, message } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '../../components/button';
import { CreateButton, ExportButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { SearchInput } from '../../components/search-input';
import { TableHeaderWithSubtitle } from '../../components/table-header-with-subtitle';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useEventStore,
} from '../../contexts/mobx';
import { Event, EventSearchParams } from '../../stores/events';
import { getEventStatus } from '../../utils/event-status';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import EventDates from './components/eventDates';
import EventImage from './components/eventImage';
import EventLocation from './components/eventLocation';
import EventRegion from './components/eventRegion';
import EventStatusDisplay from './components/eventStatus';
import EventTitle from './components/eventTitle';

const useEventExport = (events: Event[]) => {
    return React.useCallback(() => {
        if (events.length === 0) {
            message.warning('No events to export');
            return;
        }

        const csvHeaders = [
            'Title (EN)',
            'Title (FR)',
            'Subtitle (EN)',
            'Subtitle (FR)',
            'Description (EN)',
            'Description (FR)',
            'Start Date',
            'End Date',
            'Status',
            'Event Status',
            'Latitude',
            'Longitude',
            'CTA Text (EN)',
            'CTA Text (FR)',
            'Map Button Text (EN)',
            'Map Button Text (FR)',
            'Image (EN)',
            'Image (FR)',
        ];

        const csvData = events.map((event) => {
            const enTranslation = event.translations.find(
                (t) => t.language === 'en'
            );
            const frTranslation = event.translations.find(
                (t) => t.language === 'fr'
            );
            const eventStatus = getEventStatus(
                new Date(event.startDate.toDate()),
                new Date(event.endDate.toDate())
            );

            return [
                enTranslation?.title || '',
                frTranslation?.title || '',
                enTranslation?.subtitle || '',
                frTranslation?.subtitle || '',
                enTranslation?.description || '',
                frTranslation?.description || '',
                event.startDate.format('YYYY-MM-DD HH:mm:ss'),
                event.endDate.format('YYYY-MM-DD HH:mm:ss'),
                event.isActive ? 'Active' : 'Inactive',
                eventStatus || '',
                event.latitude || '',
                event.longitude || '',
                enTranslation?.ctaText || '',
                frTranslation?.ctaText || '',
                enTranslation?.mapButtonText || '',
                frTranslation?.mapButtonText || '',
                enTranslation?.image || '',
                frTranslation?.image || '',
            ];
        });

        const csvContent = [csvHeaders, ...csvData]
            .map((row) =>
                row
                    .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
                    .join(',')
            )
            .join('\n');

        // Add UTF-8 BOM for proper encoding in Excel
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
            'download',
            `events_${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [events]);
};

const EventFilters: React.FC<{
    searchValue: string;
    queryParams: EventSearchParams;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStatusChange: (value: string | undefined) => void;
    onClearFilters: () => void;
}> = React.memo(
    ({
        searchValue,
        queryParams,
        onSearchChange,
        onStatusChange,
        onClearFilters,
    }) => (
        <div className={'table_header_options_bar'}>
            <SearchInput searchValue={searchValue} onChange={onSearchChange} />
            <div style={{ width: 180 }}>
                <Select<string>
                    placeholder="Active Status"
                    value={queryParams.isActive?.toString()}
                    onChange={onStatusChange}
                    allowClear
                    style={{ width: '100%' }}
                >
                    <Select.Option value="true">Active</Select.Option>
                    <Select.Option value="false">Inactive</Select.Option>
                </Select>
            </div>
            <Button variant="secondary-destructive" onClick={onClearFilters}>
                Clear Filters
            </Button>
        </div>
    )
);
EventFilters.displayName = 'EventFilters';

export const EventsPage = observer(() => {
    const eventStore = useEventStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore(); // Used for restricting the events to the selected application
    const { hasManagementAccess } = authStore;

    const search = useSearchParams<EventSearchParams>();

    const [searchValue, setSearchValue] = React.useState<string>('');

    React.useEffect(() => {
        if (!hasManagementAccess) {
            navigation.goToHome();
        }
    }, [hasManagementAccess, navigation]);

    React.useEffect(() => {
        const paramsWithoutSearch = {
            page: search.params.page,
            perPage: search.params.perPage,
            isActive: search.params.isActive,
        };
        eventStore.fetchEvents(paramsWithoutSearch);
    }, [
        search.params.page,
        search.params.perPage,
        search.params.isActive,
        applicationSelectionStore.selectedApplication,
        eventStore,
    ]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleStatusChange = (value: string | undefined) =>
        search.setParam(
            'isActive',
            value === 'true' ? true : value === 'false' ? false : undefined
        );

    const clearFilters = () => {
        search.setParam('isActive', undefined);
        setSearchValue('');
    };

    const filteredEvents = React.useMemo(() => {
        const events = eventStore.events?.results || [];
        const searchTerm = searchValue.trim().toLowerCase();

        if (!searchTerm) return events;

        return events.filter((event) => {
            const enTranslation = event.translations.find(
                (t) => t.language === 'en'
            );
            const frTranslation = event.translations.find(
                (t) => t.language === 'fr'
            );

            const fieldsToSearch = [
                enTranslation?.title,
                enTranslation?.subtitle,
                enTranslation?.description,
                frTranslation?.title,
                frTranslation?.subtitle,
                frTranslation?.description,
                event.id.toString(),
            ];

            return fieldsToSearch.some((field) =>
                field?.toLowerCase().includes(searchTerm)
            );
        });
    }, [eventStore.events?.results, searchValue]);

    const handleExportEvents = useEventExport(filteredEvents);

    const columns: ColumnProps<Event>[] = React.useMemo(
        () => [
            {
                key: 'status',
                title: 'Status',
                render: (_, record) => <EventStatusDisplay record={record} />,
                width: 120,
                fixed: 'left',
            },
            {
                key: 'image',
                title: 'Image',
                render: (_, record) => <EventImage record={record} />,
                width: 140,
                fixed: 'left',
            },
            {
                title: 'Title',
                key: 'title',
                render: (_, record) => (
                    <EventTitle record={record} type="title" />
                ),
                className: 'title-column',
                width: 200,
            },
            {
                title: 'Subtitle',
                key: 'subtitle',
                render: (_, record) => (
                    <EventTitle record={record} type="subtitle" />
                ),
                className: 'subtitle-column',
                width: 220,
            },
            {
                title: 'Region',
                key: 'region',
                render: (_, record) => <EventRegion record={record} />,
                className: 'region-column',
                width: 225,
            },
            {
                title: (
                    <TableHeaderWithSubtitle
                        title="Event Period"
                        subtitle="Start date / End date"
                    />
                ),
                key: 'dates',
                render: (_, record) => <EventDates record={record} />,
                width: 200,
            },
            {
                title: 'Location',
                key: 'location',
                render: (_, record) => <EventLocation record={record} />,
                className: 'location-column',
                width: 200,
            },
        ],
        []
    );

    return (
        <>
            <PageTopBar title="In-Store Events">
                <ExportButton
                    onClick={handleExportEvents}
                    loading={eventStore.isExportingEvents}
                    disabled={filteredEvents.length === 0}
                >
                    Export to CSV
                </ExportButton>
                <CreateButton onClick={() => navigation.goToEventCreate()} />
            </PageTopBar>

            <div className="action_zone_above_table">
                <EventFilters
                    searchValue={searchValue}
                    queryParams={search.params}
                    onSearchChange={handleSearchChange}
                    onStatusChange={handleStatusChange}
                    onClearFilters={clearFilters}
                />
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: eventStore.events?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<Event>
                className="clickable_table_body_rows"
                scroll={{ x: 900 }}
                rowKey="id"
                dataSource={filteredEvents}
                columns={columns}
                loading={eventStore.isFetchingCount > 0}
                pagination={false}
                onRow={(record) => ({
                    onClick: () => navigation.goToEventEdit(record.id),
                })}
                size="small"
                bordered
            />
        </>
    );
});
